import { useEffect } from "react";
import { useEditorStore } from "../store";

export function useKeyboardShortcuts(onSave?: () => void) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isCmdOrCtrl = e.metaKey || e.ctrlKey;
      const target = e.target as HTMLElement;
      const isInput =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;

      // Cmd/Ctrl + Z (Undo)
      if (isCmdOrCtrl && !e.shiftKey && e.key.toLowerCase() === "z") {
        e.preventDefault();
        useEditorStore.getState().undo();
        return;
      }

      // Cmd/Ctrl + Shift + Z or Cmd/Ctrl + Y (Redo)
      if (
        (isCmdOrCtrl && e.shiftKey && e.key.toLowerCase() === "z") ||
        (isCmdOrCtrl && e.key.toLowerCase() === "y")
      ) {
        e.preventDefault();
        useEditorStore.getState().redo();
        return;
      }

      // Cmd/Ctrl + S (Save)
      if (isCmdOrCtrl && e.key.toLowerCase() === "s") {
        e.preventDefault();
        onSave?.();
        return;
      }

      // Cmd/Ctrl + D (Duplicate node)
      if (isCmdOrCtrl && e.key.toLowerCase() === "d") {
        const selectedId = useEditorStore.getState().selectedNodeId;
        if (selectedId) {
          e.preventDefault();
          useEditorStore.getState().duplicateNode(selectedId);
        }
        return;
      }

      // Cmd/Ctrl + = / - (Zoom in / out), Cmd/Ctrl + 0 (100%)
      if (isCmdOrCtrl && (e.key === "=" || e.key === "+" || e.key === "-")) {
        e.preventDefault();
        useEditorStore.getState().zoomBy(e.key === "-" ? -1 : 1);
        return;
      }
      if (isCmdOrCtrl && e.key === "0") {
        e.preventDefault();
        useEditorStore.getState().setZoom(1);
        return;
      }

      // Shift + 1 (Zoom to fit, when not typing)
      if (!isInput && e.shiftKey && !isCmdOrCtrl && e.code === "Digit1") {
        e.preventDefault();
        useEditorStore.getState().setZoom("fit");
        return;
      }

      // Escape (Deselect)
      if (e.key === "Escape") {
        useEditorStore.getState().selectNode(null);
        return;
      }

      // Delete / Backspace (Delete node when not typing in input)
      if (!isInput && (e.key === "Delete" || e.key === "Backspace")) {
        const selectedId = useEditorStore.getState().selectedNodeId;
        if (selectedId) {
          e.preventDefault();
          useEditorStore.getState().removeNode(selectedId);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onSave]);
}
